import { useEffect, useRef } from 'react'
import { markPlayerSeen, markPlayerSeenBeacon } from '../services/api'

/**
 * How often the presence heartbeat advances the catch-up anchor while the
 * app is open. Matches the backend de-dupe window being far smaller (30s),
 * so redundant beats are harmless.
 */
export const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000

/**
 * Presence heartbeat for the offline catch-up anchor (plan Slice 5+).
 *
 * `Player.last_seen_at` is what "while you were away" measures absence from.
 * Without this hook the digest consume is its only writer, so a session the
 * player lived through replays as a fake absence on the next page load.
 * While authenticated this hook:
 *  - beats every HEARTBEAT_INTERVAL_MS while the app stays open
 *  - sends a keepalive beacon on page hide (close/refresh) and on the tab
 *    going hidden, so the anchor lands on the actual last-seen moment
 *
 * There is deliberately NO beat at mount: the mount-time digest fetch already
 * consumes and rewrites the anchor, and a heartbeat racing it could commit
 * first — the digest refetches the anchor under the shared lock and would
 * then read "away = 0s", erasing a real absence story before it is told.
 * (If the digest fetch is skipped or fails, the first interval beat covers
 * the anchor within five minutes.) An open-but-backgrounded tab still counts
 * as present (idle-game semantics: the world stayed live on screen).
 * Failures are swallowed — presence is best-effort and must never surface
 * UI noise.
 */
export const usePresenceHeartbeat = (enabled) => {
  const enabledRef = useRef(enabled)
  enabledRef.current = enabled

  useEffect(() => {
    if (!enabled) return undefined

    let cancelled = false

    const beat = () => {
      if (cancelled || !enabledRef.current) return
      markPlayerSeen().catch(() => {})
    }

    const sendBeacon = () => {
      if (cancelled || !enabledRef.current) return
      markPlayerSeenBeacon()
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') sendBeacon()
    }

    const intervalId = setInterval(beat, HEARTBEAT_INTERVAL_MS)
    window.addEventListener('pagehide', sendBeacon)
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      cancelled = true
      clearInterval(intervalId)
      window.removeEventListener('pagehide', sendBeacon)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [enabled])
}

export default usePresenceHeartbeat
